import React, { Component } from 'react';

// Components and Others
import clsx from 'clsx';
import { map } from 'lodash';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import StringUtils from 'lib/StringUtils';
import DealStyles from 'styles/modules/DealStyles';
import { ReactComponent as DeleteIcon } from 'assets/remove.svg';
import InputNumber from 'components/widgets/InputNumber';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import DialogActions from 'components/widgets/modal/DialogActions';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import WeOweContainer from 'components/containers/deals/create/dealTab/WeOweContainer';

import { isValidField, isValidSchema } from 'utils/schema/utils';
import { WeOweSchema, WeOweRowSchema } from 'utils/schema/deal/WeOwe';

// Material UI
import { withStyles, fade } from '@material-ui/core/styles';
import {
    Grid, DialogContent,
    IconButton,
    Dialog,
} from '@material-ui/core';
import { MEDIA_QUERY } from 'utils/enum/Core';

const styles = (theme) => ({
    ...DealStyles.dealStyles(theme, fade),
    containerHeaderDesktop: {
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            '& .MuiGrid-item': {
                padding: 0,
                textAlign: 'center',
                '& label': {
                    marginBottom: 0,
                },
            },
        },
    },
    addButtonMobileWrapper: {
        display: 'none',
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            display: 'block',
        },
    },
    addButtonDesktop: {
        display: 'block',
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            display: 'none',
        },
    },
    rowMobile: {
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            flexDirection: 'column',
            '& > .MuiGrid-item': {
                width: '100%',
                maxWidth: '100%',
            },
        },
    },
    deleteButtonMobile: {
        display: 'none',
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            padding: theme.spacing(1, 0),
        },
    },
    deleteButtonDesktop: {
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            display: 'none',
        },
    },
    labelHeaderDesktop: {
        display: 'block',
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            display: 'none',
        },
    },
    labelInputMobile: {
        display: 'none',
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            display: 'block',
        },
    },
    weOweDialogAppBar: {
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            '& button[aria-label="Close"]': {
                display: 'none !important',
            },
        },
    },
});

// TODO: Change the quantity, part, labor fields when integrating the component to type of type number
class WeOweDialog extends Component {
    componentDidMount() {
        const { props: { getServicesData } } = this;
        getServicesData();
    }

    onChangeIntegerValue = (field, value, index) => {
        const { props: { onChangeValue } } = this;
        const currentValue = StringUtils.isEmpty(value) ? 0 : parseInt(value, 10);

        onChangeValue(field, currentValue, index);
    }

    renderRow() {
        const {
            props: {
                classes, records, onChangeValue, showConfirm, writePermission,
            },
        } = this;

        return map(records, (item, index) => {
            const {
                quantity, nameOfItem, part, labor, currentAccountNumber,
            } = item;
            const total = (quantity * part) + labor;

            const isValidData = isValidSchema(WeOweRowSchema, item);
            const { errors } = isValidData;

            return (
                <Grid
                    container
                    spacing={2}
                    className={clsx(classes.rowWeOwe, classes.rowMobile, 'show-icon-when-hovering')}
                    key={`${currentAccountNumber}${index}`}
                >
                    <Grid item xs={12} md={7}>
                        <Grid container spacing={2}>
                            <Grid item xs={4} md={4}>
                                <InputNumber
                                    required
                                    value={quantity}
                                    className={isValidField(errors, 'quantity') ? 'invalid-field' : ''}
                                    onChange={(value) => this.onChangeIntegerValue('quantity', value, index)}
                                    placeholder="0"
                                    decimalScale={0}
                                    thousandSeparator
                                    disabled={!writePermission}
                                    size="sm"
                                />
                            </Grid>
                            <Grid item xs={8} md={8}>
                                <Form.Control
                                    value={nameOfItem}
                                    className={isValidField(errors, 'nameOfItem') ? 'invalid-field' : ''}
                                    onChange={(e) => onChangeValue('nameOfItem', e.target.value, index)}
                                    type="text"
                                    placeholder=""
                                    disabled={!writePermission}
                                    size="sm"
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Grid container spacing={2}>
                            <Grid item xs={4} md={4}>
                                <Form.Label className={clsx(classes.weOweLabels, classes.labelInputMobile)}>Part</Form.Label>
                                <InputNumber
                                    value={part}
                                    className={isValidField(errors, 'part') ? 'invalid-field' : ''}
                                    onChange={(value) => onChangeValue('part', value, index)}
                                    showCurrency
                                    thousandSeparator
                                    placeholder="0.00"
                                    disabled={!writePermission}
                                    size="sm"
                                />
                            </Grid>
                            <Grid item xs={4} md={4}>
                                <Form.Label className={clsx(classes.weOweLabels, classes.labelInputMobile)}>Labor</Form.Label>
                                <InputNumber
                                    value={labor}
                                    className={isValidField(errors, 'labor') ? 'invalid-field' : ''}
                                    onChange={(value) => onChangeValue('labor', value, index)}
                                    showCurrency
                                    thousandSeparator
                                    placeholder="0.00"
                                    disabled={!writePermission}
                                    size="sm"
                                />
                            </Grid>
                            <Grid item xs={4} md={4}>
                                <Form.Label className={clsx(classes.weOweLabels, classes.labelInputMobile)}>Total</Form.Label>
                                <InputNumber
                                    value={total || ''}
                                    placeholder="0.00"
                                    onChange={(value) => onChangeValue('total', value, index)}
                                    showCurrency
                                    thousandSeparator
                                    disabled
                                    size="sm"
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    {writePermission && (
                        <>
                            <div className={clsx('with-icon-right', classes.deleteButtonDesktop)}>
                                <IconButton
                                    aria-label="delete"
                                    className={clsx(classes.textError, 'show-hidden-item')}
                                    onClick={() => showConfirm(index)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </div>
                            <div className={classes.deleteButtonMobile}>
                                <IconButton
                                    aria-label="delete"
                                    className={classes.textError}
                                    onClick={() => showConfirm(index)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </div>
                        </>
                    )}
                </Grid>
            );
        });
    }

    render() {
        const {
            props: {
                toggleModal, classes, addRow, open, onSave, allowSave,
                openConfirm, onDelete, cancelConfirm, isSaving, records,
                writePermission,
            },
        } = this;

        const isValidData = isValidSchema(WeOweSchema, records);
        const { isValid } = isValidData;

        // Button for mobile header
        const addButtonMobile = writePermission ? (
            <div className={classes.addButtonMobileWrapper}>
                <IconButton
                    aria-label="add"
                    className={classes.textSuccess}
                    onClick={addRow}
                >
                    <AddCircleIcon />
                </IconButton>
            </div>
        ) : null;

        return (
            <Dialog
                open={open}
                fullWidth
                className={classes.weOweDialogWidth}
                maxWidth="md"
                scroll="paper"
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogAppBar
                    title="We Owe"
                    onClose={toggleModal}
                    secondaryButton={addButtonMobile}
                    appBarClassName={classes.weOweDialogAppBar}
                />
                <DialogContent className={classes.dialogContentWeOwe}>
                    <Grid container spacing={2} className={clsx(classes.rowWeOwe, classes.rowMobile)}>
                        <Grid item xs={12} md={7}>
                            <Grid container spacing={2} className={classes.containerHeaderDesktop}>
                                <Grid item xs={4} md={4}>
                                    <Form.Label className={classes.weOweLabels}>QTY</Form.Label>
                                </Grid>
                                <Grid item xs={8} md={8}>
                                    <Form.Label>Description</Form.Label>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Grid container spacing={2}>
                                <Grid item xs={4} md={4}>
                                    <Form.Label className={clsx(classes.weOweLabels, classes.labelHeaderDesktop)}>Part</Form.Label>
                                </Grid>
                                <Grid item xs={4} md={4}>
                                    <Form.Label className={clsx(classes.weOweLabels, classes.labelHeaderDesktop)}>Labor</Form.Label>
                                </Grid>
                                <Grid item xs={4} md={4}>
                                    <Form.Label className={clsx(classes.weOweLabels, classes.labelHeaderDesktop)}>Total</Form.Label>
                                </Grid>
                            </Grid>
                        </Grid>
                        <div className={clsx(classes.boxIconDelete, classes.deleteButtonDesktop)} />
                    </Grid>
                    {this.renderRow()}
                    {writePermission && (
                        <div className={classes.addButtonDesktop}>
                            <IconButton aria-label="add" className={clsx(classes.textSuccess, classes.addWeOweIcon)} onClick={addRow}>
                                <AddCircleIcon />
                            </IconButton>
                        </div>
                    )}
                </DialogContent>
                {writePermission && (
                    <DialogActions
                        disablePrimaryButton={!allowSave || isSaving || !isValid}
                        titlePrimary="Save"
                        onClickSecondary={toggleModal}
                        onClickPrimary={() => {
                            onSave();
                        }}
                    />
                )}
                <ConfirmDialog
                    title="Confirm remove line"
                    description="Are you sure you want to remove this line?"
                    open={openConfirm}
                    showHeader
                    dividerFooter={false}
                    titlePrimary="Yes"
                    titleSecondary="No"
                    variant="outlined"
                    onClickPrimary={onDelete}
                    onClose={cancelConfirm}
                    onClickSecondary={cancelConfirm}
                />
            </Dialog>
        );
    }
}

WeOweDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    allowSave: PropTypes.bool.isRequired,
    addRow: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    openConfirm: PropTypes.bool.isRequired,
    showConfirm: PropTypes.func.isRequired,
    cancelConfirm: PropTypes.func.isRequired,
    toggleModal: PropTypes.func.isRequired,
    onChangeValue: PropTypes.func.isRequired,
    getServicesData: PropTypes.func.isRequired,
    records: PropTypes.arrayOf(PropTypes.object).isRequired,
    classes: PropTypes.oneOfType([PropTypes.object]).isRequired,
    isSaving: PropTypes.bool.isRequired,
    writePermission: PropTypes.bool,
};

WeOweDialog.defaultProps = {
    writePermission: false,
};

export default withStyles(styles)(WeOweContainer(WeOweDialog));
